import { FC } from 'react'
import moment from 'moment'
import { cn } from 'src/utils/cn'
import styles from './comparisonFilters.module.scss'
import { PeriodPicker } from '../PeriodPicker'

interface ComparisonFiltersProps {
  // Highlighted shortcut, e.g. 'Previous Month' on a generated comparison.
  activeQuickCompare?: string
  // The comparison period once one is chosen; the placeholder until then.
  compareWith?: string
  // A generated report is a snapshot of the two periods it was built from, so
  // its filters are shown dimmed and inert rather than inviting an edit that
  // would not change anything.
  disabled?: boolean
  // 'row' spans the report's full width; 'stacked' drops the primary period
  // onto its own line, which is how the New Comparison modal lays it out.
  layout?: 'row' | 'stacked'
  // Left unset the comparison period is read-only, which is how the generated
  // report shows the run it was built from.
  onCompareApply?: (start: moment.Moment, end: moment.Moment) => void
  onPrimaryApply: (start: moment.Moment, end: moment.Moment) => void
  // Left unset the shortcuts are read-only, as they are on a generated report.
  onQuickCompare?: (option: QuickCompareOption) => void
  primaryRange?: string
}

const COPY = {
  compareWith: 'Compare With',
  primaryPeriod: 'Primary Period',
  quickCompare: 'Quick Compare',
  selectComparisonPeriod: 'Select comparison period',
  selectDateRange: 'Select date range',
}

// Each shortcut is the primary period shifted back by its own span, so the
// two ranges are always the same length and directly comparable.
export interface QuickCompareOption {
  label: string
  months: number
}

const QUICK_COMPARE_OPTIONS: QuickCompareOption[] = [
  { label: 'Previous Month', months: 1 },
  { label: 'Previous Quarter', months: 3 },
  { label: 'Previous Half Year', months: 6 },
  { label: 'Previous Year', months: 12 },
]

export const ComparisonFilters: FC<ComparisonFiltersProps> = ({
  activeQuickCompare,
  compareWith,
  disabled = false,
  layout = 'row',
  onCompareApply,
  onPrimaryApply,
  onQuickCompare,
  primaryRange,
}) => {
  // The three groups are identical in both layouts; only their arrangement
  // differs, so they are built once and placed below.
  const primaryGroup = (
    <div className={styles.filterGroup}>
      <span className={styles.filterLabel}>{COPY.primaryPeriod}</span>
      <PeriodPicker
        disabled={disabled}
        onApply={onPrimaryApply}
        placeholder={COPY.selectDateRange}
        value={primaryRange}
      />
    </div>
  )

  // The same picker as the primary period — the only difference is which end
  // of the comparison it sets.
  const compareGroup = (
    <div className={styles.filterGroup}>
      <span className={styles.filterLabel}>{COPY.compareWith}</span>
      <PeriodPicker
        disabled={disabled}
        onApply={onCompareApply ?? (() => undefined)}
        placeholder={COPY.selectComparisonPeriod}
        value={compareWith}
      />
    </div>
  )

  const quickGroup = (
    <div className={styles.filterGroup}>
      <span className={styles.filterLabel}>{COPY.quickCompare}</span>
      <div className={cn(styles.quickCompare, disabled && styles.disabled)}>
        {QUICK_COMPARE_OPTIONS.map((option) => (
          <button
            className={cn(
              styles.quickCompareOption,
              option.label === activeQuickCompare &&
                styles.quickCompareOptionActive
            )}
            disabled={disabled}
            key={option.label}
            onClick={() => onQuickCompare?.(option)}
            type="button"
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )

  if (layout === 'stacked') {
    return (
      <div className={styles.stacked}>
        {primaryGroup}

        <div className={styles.stackedRow}>
          {compareGroup}
          <span className={styles.filterDivider} />
          {quickGroup}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.filters}>
      {primaryGroup}
      <span className={styles.filterDivider} />
      {compareGroup}
      <span className={styles.filterDivider} />
      {quickGroup}
    </div>
  )
}
